import type { TrackerCopy } from './types'

/** Contenido del tracker en francés (mercado francés). */
export const TRACKER_COPY_FR: TrackerCopy = {
  meta: {
    title: 'Où est le Père Noël ? Carte et compte à rebours de Noël',
    description:
      'Découvrez combien de temps il reste avant Noël, où se trouve le Père Noël et à quelle heure il arrivera. Suivez son voyage sur une carte du monde interactive.',
    keywords: [
      'où est le père noël',
      'ou est pere noel',
      'compte à rebours noël',
      'compte a rebours noel',
      'quand arrive le père noël',
      'à quelle heure passe le père noël',
      'voyage du père noël',
      'carte du père noël',
      'suivre le père noël',
      'traquer le père noël',
      'santa tracker',
      'combien de jours avant noël',
    ],
    schemaName: 'Carte et compte à rebours du Père Noël',
  },

  hero: {
    badge: {
      preparing: 'Atelier du pôle Nord',
      liftoff: 'Derniers préparatifs',
      traveling: 'Livraisons en cours',
      completed: 'Mission accomplie',
    },
    h1: {
      preparing: 'Combien de temps avant l’arrivée du Père Noël ?',
      liftoff: 'Le Père Noël s’apprête à décoller',
      traveling: 'Où est le Père Noël en ce moment ?',
      completed: 'Le Père Noël est déjà rentré au pôle Nord',
    },
    intro: {
      preparing:
        'Au pôle Nord, on ne chôme pas : les lutins emballent les cadeaux, les rennes s’entraînent et la grande liste est vérifiée encore et encore. Ici, vous pouvez voir combien de temps il reste avant Noël et comment avancent les préparatifs.',
      liftoff:
        'Il ne reste que quelques heures avant le plus long voyage de l’année. Le traîneau est chargé, Rudolph a allumé son nez et au pôle Nord, personne ne ferme l’œil.',
      traveling:
        'C’est le réveillon de Noël et le traîneau fait le tour du monde. Suivez sa route sur la carte, regardez par où il vient de passer et découvrez quand il passera chez vous.',
      completed:
        'Tous les cadeaux sont livrés et les rennes se reposent au pôle Nord. Le prochain Noël est déjà en préparation : voici le compte à rebours.',
    },
    toMap: 'Voir la carte',
  },

  countdown: {
    heading: {
      preparing: 'Le Père Noël arrivera chez vous dans',
      liftoff: 'Le traîneau décolle dans',
      traveling: 'Il arrive chez vous dans',
      completed: 'Le prochain Noël est dans',
    },
    units: { days: 'jours', hours: 'heures', minutes: 'minutes', seconds: 'secondes' },
    christmasNote: 'Noël, c’est le 25 décembre : il reste {days} jours.',
    christmasToday: 'Aujourd’hui, c’est Noël !',
    arrivingNow: 'Il survole votre région !',
    ariaLabel: 'Compte à rebours de l’arrivée du Père Noël',
  },

  status: {
    preparing: {
      far: [
        'Les lutins préparent déjà les premiers jouets de l’année.',
        'Le Père Noël a ouvert un nouveau carnet pour la grande liste.',
        'Dans l’atelier, on teste les idées cadeaux pour le prochain Noël.',
        'Les rennes rentrent petit à petit de leurs vacances.',
      ],
      months: [
        'L’atelier du pôle Nord travaille sans relâche.',
        'Le Père Noël relit sa grande liste.',
        'Les rennes ont commencé leur entraînement.',
        'Les premières lettres arrivent du monde entier.',
      ],
      weeks: [
        'La Mère Noël prépare des biscuits pour tout l’atelier.',
        'Le Père Noël relit la liste pour la deuxième fois.',
        'Les lutins emballent les cadeaux à toute vitesse.',
        'Le traîneau est à l’atelier, prêt pour sa révision.',
      ],
      days: [
        'Le décollage du traîneau est imminent !',
        'Les lutins ferment les derniers sacs.',
        'Rudolph s’entraîne pour la grande nuit.',
        'Au pôle Nord, plus personne ne dort.',
      ],
      hours: [
        'Le Père Noël charge les derniers cadeaux.',
        'Le traîneau est prêt au décollage.',
        'Les rennes sont déjà attelées et attendent le signal.',
        'Rudolph a déjà allumé son nez.',
      ],
    },
    liftoff: [
      'Le Père Noël charge les derniers cadeaux.',
      'Rudolph a déjà allumé son nez.',
      'Les lutins referment le grand sac.',
      'Le traîneau est prêt au décollage.',
    ],
    traveling: [
      'Le traîneau vient de passer par {city}.',
      'Le Père Noël quitte {city} avec un sac un peu plus léger.',
      'Rudolph guide le traîneau après l’étape de {city}.',
      'À {city}, ils ont déjà reçu leurs cadeaux.',
    ],
    completed: [
      'Le Père Noël a terminé sa tournée.',
      'Tous les cadeaux sont livrés.',
      'Les rennes se reposent déjà au pôle Nord.',
      'Le prochain Noël est déjà en préparation.',
    ],
    lastVisited: 'Dernier lieu survolé',
    nextStop: 'Prochaine étape',
    flyingTo: 'En vol vers {leg}',
    atNorthPole: 'Au pôle Nord',
    backHome: 'De retour à la maison',
    legLabel: {
      'north-pole': 'le pôle Nord',
      pacific: 'l’océan Pacifique',
      oceania: 'l’Océanie',
      'east-asia': 'l’Asie de l’Est',
      'southeast-asia': 'l’Asie du Sud-Est',
      'central-asia': 'l’Asie centrale et méridionale',
      'middle-east': 'le Moyen-Orient',
      'eastern-europe': 'l’Europe de l’Est',
      africa: 'l’Afrique',
      'western-europe': 'l’Europe de l’Ouest',
      'south-america': 'l’Amérique du Sud',
      'north-america': 'l’Amérique du Nord',
      'north-pole-return': 'le pôle Nord',
    },
  },

  map: {
    heading: 'Le voyage du Père Noël',
    subheading: 'Touchez le traîneau ou une ville pour en savoir plus.',
    ariaLabel: 'Carte du monde avec le voyage du Père Noël',
    sleighLabel: 'Traîneau du Père Noël',
    northPoleLabel: 'Pôle Nord',
    legend: { visited: 'Déjà passé', current: 'Il est là', upcoming: 'Pas encore' },
    stopStatus: {
      visited: 'Le Père Noël est déjà passé par ici',
      current: 'Le Père Noël est ici maintenant',
      upcoming: 'Le Père Noël n’est pas encore arrivé',
    },
    localTime: 'Heure locale : {time}',
    close: 'Fermer',
    followSleigh: 'Suivre le traîneau',
    wholeWorld: 'Voir le monde entier',
    listHeading: 'Le voyage, étape par étape',
    listIntro:
      'Le même voyage sous forme de liste, si vous préférez le lire ou si la carte ne s’affiche pas bien sur votre écran.',
    parentsNote:
      'Note pour les parents : ceci est une expérience de Noël simulée. L’itinéraire se calcule à partir des fuseaux horaires ; ce n’est ni une position réelle ni un suivi GPS.',
  },

  eta: {
    heading: 'À quelle heure arrive-t-il chez vous ?',
    body: 'Le Père Noël arrivera dans votre région approximativement entre {start} et {end}.',
    zone: 'Calculé pour votre fuseau horaire ({zone}).',
    remaining: 'Il reste {countdown}.',
    passed: 'Le Père Noël est déjà passé chez vous. Regardez sous le sapin !',
    unknownZone: 'Calculé avec l’heure de votre appareil.',
    disclaimer:
      'C’est une fourchette indicative, pas une heure garantie : le Père Noël préfère passer quand tout le monde dort.',
  },

  stats: {
    heading: {
      preparing: 'En ce moment à l’atelier',
      liftoff: 'Tout est prêt pour la grande nuit',
      traveling: 'Le voyage en chiffres',
      completed: 'Comment s’est passé le voyage',
    },
    labels: {
      giftsReady: 'Cadeaux préparés',
      giftsDelivered: 'Cadeaux livrés',
      lettersReceived: 'Lettres reçues',
      elvesWorking: 'Lutins au travail',
      reindeerTraining: 'Rennes à l’entraînement',
      kilometers: 'Kilomètres',
      citiesVisited: 'Villes visitées',
      countriesVisited: 'Pays visités',
      homesWaiting: 'Foyers en attente',
      travelMinutes: 'Temps de vol',
    },
    units: { kilometers: 'km', hours: 'h' },
  },

  workshop: {
    heading: 'En ce moment au pôle Nord',
    activities: {
      preparing: [
        'Les lutins emballent les cadeaux.',
        'Le Père Noël lit les lettres.',
        'Les rennes s’entraînent sur la piste de décollage.',
        'La Mère Noël prépare les biscuits.',
        'Le traîneau passe sa révision annuelle.',
        'Dans la salle de la grande liste, on vérifie les prénoms.',
      ],
      liftoff: [
        'Le traîneau passe sa dernière révision.',
        'Rudolph s’entraîne pour la grande nuit.',
        'Les lutins chargent les sacs sur le traîneau.',
        'La Mère Noël prépare le chocolat chaud du voyage.',
      ],
      traveling: [
        'Les lutins suivent le voyage depuis la tour de contrôle.',
        'La Mère Noël laisse la cheminée allumée en attendant le retour.',
        'À l’atelier, on ramasse les dernières étiquettes cadeaux.',
        'Les rennes de réserve attendent au cas où un changement serait nécessaire.',
      ],
      completed: [
        'Les rennes se reposent dans l’écurie du pôle Nord.',
        'La Mère Noël sert le chocolat de l’après-voyage.',
        'Les lutins rangent l’atelier pour l’année prochaine.',
        'Le Père Noël range la grande liste jusqu’au prochain Noël.',
      ],
    },
    night: [
      'Au pôle Nord, il fait nuit : l’atelier travaille en lumière tamisée.',
      'Les lutins du service de nuit emballent en silence.',
      'Le Père Noël dort un peu avant le grand jour.',
      'On n’entend que le craquement de la neige autour de l’atelier.',
    ],
  },

  surprises: {
    northPole: 'Vous avez réveillé l’aurore boréale ! Les lutins vous saluent depuis l’atelier.',
    celebration: 'Voyage terminé ! Joyeux Noël.',
  },

  controls: {
    soundOn: 'Activer le son',
    soundOff: 'Couper le son',
    fullscreenEnter: 'Plein écran',
    fullscreenExit: 'Quitter le plein écran',
    share: 'Partager',
    shareCopied: 'Lien copié',
    motionOn: 'Activer les animations',
    motionOff: 'Réduire les animations',
  },

  share: {
    title: 'Où est le Père Noël ?',
    text: {
      preparing: 'Découvrez combien de temps il reste avant l’arrivée du Père Noël.',
      liftoff: 'Le Père Noël s’apprête à décoller !',
      traveling: 'Regardez où va le Père Noël !',
      completed: 'Le Père Noël a terminé sa tournée. Joyeux Noël !',
    },
  },

  faq: {
    heading: 'Questions fréquentes',
    items: [
      {
        question: 'Combien de temps avant Noël ?',
        answer:
          'Le compte à rebours de cette page vous le dit à la seconde. Noël se fête le 25 décembre, et le Père Noël part du pôle Nord la nuit du 24.',
      },
      {
        question: 'Où est le Père Noël en ce moment ?',
        answer:
          'Ça dépend du jour. La majeure partie de l’année, il est au pôle Nord à préparer Noël, et le réveillon il fait le tour du monde. La carte de cette page montre où il doit se trouver à chaque instant.',
      },
      {
        question: 'Par où passe le Père Noël ?',
        answer:
          'Il commence par le Pacifique, poursuit par l’Océanie et l’Asie, traverse le Moyen-Orient, l’Europe et l’Afrique, descend en Amérique du Sud et termine en Amérique du Nord avant de rentrer chez lui. Il suit la nuit : il livre là où il fait déjà noir.',
      },
      {
        question: 'Quand part le Père Noël du pôle Nord ?',
        answer:
          'Il décolle dans l’après-midi du 24 décembre, quand il est presque minuit dans les îles du Pacifique. De là, il ne s’arrête pas jusqu’au matin du 25.',
      },
      {
        question: 'À quelle heure arrive le Père Noël ?',
        answer:
          'Dans chaque foyer, il arrive quand les enfants dorment déjà, grosso modo entre 21 h et minuit. Sur cette page, vous voyez la fourchette indicative calculée pour votre fuseau horaire.',
      },
      {
        question: 'Le Père Noël passe-t-il dans tous les pays ?',
        answer:
          'Oui : les 195 pays du monde figurent sur son itinéraire. Sur la carte, certaines villes servent de repère, mais le traîneau passe vraiment partout.',
      },
      {
        question: 'Comment fait-il le tour du monde en une seule nuit ?',
        answer:
          'Parce qu’il voyage vers l’ouest en suivant la nuit : en allant dans le même sens que la rotation de la Terre, le réveillon lui dure plus de trente heures. Ça, et un peu de magie du pôle Nord.',
      },
      {
        question: 'Que se passe-t-il si je suis réveillé quand il arrive ?',
        answer:
          'Le Père Noël est très discret : s’il remarque que quelqu’un est réveillé, il continue sa route et revient plus tard. Le mieux pour qu’il passe vite, c’est d’aller se coucher tôt.',
      },
      {
        question: 'Comment sait-il où j’habite ?',
        answer:
          'Grâce aux lettres. Chaque lettre qui arrive au pôle Nord est notée dans la grande liste avec le prénom et la ville, et les lutins préparent l’itinéraire avec ces informations.',
      },
      {
        question: 'Quand rentre-t-il au pôle Nord ?',
        answer:
          'Le matin du 25 décembre, après la dernière livraison. Les rennes se reposent quelques jours et l’atelier commence à penser au Noël suivant.',
      },
      {
        question: 'La carte montre-t-elle sa position réelle ?',
        answer:
          'Non. C’est une expérience de Noël simulée : l’itinéraire se calcule à partir des fuseaux horaires pour coïncider avec la nuit de chaque endroit. Aucun suivi GPS ni position réelle, et cette page ne demande jamais votre localisation.',
      },
      {
        question: 'Comment puis-je parler avec le Père Noël ?',
        answer:
          'Sur PapaNoel.chat, vous pouvez préparer un appel où le Père Noël parle directement avec votre enfant, par son prénom et en temps réel. C’est vous qui décidez ce qu’il sait à l’avance.',
      },
      {
        question: 'Peut-on faire une visio avec le Père Noël ?',
        answer:
          'Oui. En plus de l’appel vocal, vous pouvez faire une visio personnalisée où le Père Noël connaît le prénom, les goûts, les réussites et les vœux de votre enfant.',
      },
    ],
  },

  cta: {
    heading: 'Pendant que vous attendez son arrivée, vous pouvez parler avec le Père Noël',
    body: 'Préparez une visio personnalisée où le Père Noël connaîtra le prénom, les goûts, les réussites et les vœux de votre enfant.',
    primary: 'Parler avec le Père Noël',
    secondary: 'Découvrir les personnages magiques',
    free: 'Envie d’essayer d’abord ? Parlez gratuitement au Père Noël',
    travelingNote:
      'Ce soir, le Père Noël est très occupé, mais vous pouvez encore préparer une visio magique.',
  },

  stops: {
    'north-pole': 'Pôle Nord',
    'north-pole-return': 'Pôle Nord',
    auckland: 'Auckland',
    suva: 'Suva',
    sydney: 'Sydney',
    melbourne: 'Melbourne',
    brisbane: 'Brisbane',
    'port-moresby': 'Port Moresby',
    darwin: 'Darwin',
    tokyo: 'Tokyo',
    seoul: 'Séoul',
    shanghai: 'Shanghai',
    beijing: 'Pékin',
    'hong-kong': 'Hong Kong',
    manila: 'Manille',
    singapore: 'Singapour',
    jakarta: 'Jakarta',
    bangkok: 'Bangkok',
    dhaka: 'Dacca',
    kathmandu: 'Katmandou',
    'new-delhi': 'New Delhi',
    karachi: 'Karachi',
    kabul: 'Kaboul',
    dubai: 'Dubaï',
    tehran: 'Téhéran',
    riyadh: 'Riyad',
    istanbul: 'Istanbul',
    moscow: 'Moscou',
    athens: 'Athènes',
    warsaw: 'Varsovie',
    cairo: 'Le Caire',
    johannesburg: 'Johannesburg',
    lagos: 'Lagos',
    berlin: 'Berlin',
    rome: 'Rome',
    paris: 'Paris',
    madrid: 'Madrid',
    london: 'Londres',
    lisbon: 'Lisbonne',
    reykjavik: 'Reykjavík',
    'rio-de-janeiro': 'Rio de Janeiro',
    'sao-paulo': 'São Paulo',
    'buenos-aires': 'Buenos Aires',
    santiago: 'Santiago du Chili',
    'la-paz': 'La Paz',
    caracas: 'Caracas',
    bogota: 'Bogota',
    lima: 'Lima',
    'new-york': 'New York',
    toronto: 'Toronto',
    havana: 'La Havane',
    'mexico-city': 'Mexico',
    chicago: 'Chicago',
    denver: 'Denver',
    'los-angeles': 'Los Angeles',
    vancouver: 'Vancouver',
    anchorage: 'Anchorage',
    honolulu: 'Honolulu',
  },
}